import { ListFilter } from 'lucide-react';

import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/Select/Select';
import { cn } from '@/util/utils';

export interface FilterOption<K extends string> {
    value: K;
    label: string;
    /** Read after the label as "label · detail", and kept whole when a long label is cut. */
    detail?: string;
}

function fullText({ label, detail }: { label: string; detail?: string }) {
    return detail === undefined ? label : `${label} · ${detail}`;
}

export interface FilterSelectProps<K extends string> {
    /** The accessible name; the visible value says the rest. */
    label: string;
    value: K;
    options: readonly FilterOption<K>[];
    onChange: (value: K) => void;
    className?: string;
}

/**
 * The toolbar's filter (the design's ui-select with the list-filter icon): a standard-height select that
 * always shows its current choice, "All groups" and the like included, so the state of the
 * register is never hidden behind a placeholder.
 */
function FilterSelect<K extends string>({
    label,
    value,
    options,
    onChange,
    className,
}: FilterSelectProps<K>) {
    const chosen = options.find((option) => option.value === value);
    return (
        <Select value={value} onValueChange={(next) => onChange(next as K)}>
            <SelectTrigger
                icon={ListFilter}
                aria-label={label}
                className={cn('min-w-0 sm:w-[210px] sm:flex-none', className)}
            >
                <SelectValue title={chosen && fullText(chosen)}>
                    {/* The list keeps a detail whole, but the trigger is too narrow for that: the
                        label would get nothing. Here the label takes up to half and the detail
                        is cut in the rest (ALS-484). Undefined keeps Radix's copy of the item. */}
                    {chosen?.detail === undefined ? undefined : (
                        <span className="flex min-w-0 flex-1">
                            <span className="max-w-1/2 shrink-0 truncate">{chosen.label}</span>
                            <span className="shrink-0 whitespace-pre"> · </span>
                            <span className="min-w-0 truncate">{chosen.detail}</span>
                        </span>
                    )}
                </SelectValue>
            </SelectTrigger>
            <SelectContent align="start">
                {options.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                        <span className="flex-initial! truncate">{option.label}</span>
                        {/* The row stretches every span (flex-1); a detail keeps its width so
                            only the label is cut (ALS-484). The separator is a bare text node:
                            a span's edge spaces are trimmed from the accessible name. */}
                        {option.detail === undefined ? null : (
                            <>
                                {' · '}
                                <span className="flex-none!">{option.detail}</span>
                            </>
                        )}
                    </SelectItem>
                ))}
            </SelectContent>
        </Select>
    );
}

export { FilterSelect };
